<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Overlay avec fond flou -->
    <div class="fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity" @click="closeModal"></div>
    
    <!-- Modal -->
    <div class="flex min-h-full items-center justify-center p-4">
      <div class="relative bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        <!-- Header -->
        <div class="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 class="text-xl font-semibold text-gray-900">
              Ajouter des mémos à la collection
            </h2>
            <p class="text-sm text-gray-500 mt-1">
              Sélectionnez les mémos que vous souhaitez ajouter à "{{ collectionName }}"
            </p>
          </div>
          <button
            class="text-gray-400 hover:text-gray-600 transition-colors"
            @click="closeModal"
          >
            <XIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-hidden flex flex-col">
          <!-- Toolbar -->
          <div class="p-4 border-b border-gray-200 bg-gray-50">
            <div class="flex items-center justify-between gap-4">
              <!-- Filtre Mes mémos / Tous les mémos -->
              <div class="flex items-center space-x-2">
                <button
                  class="px-3 py-2 text-sm font-medium rounded-md transition-colors"
                  :class="memoFilter === 'my' ? 'bg-blue-100 text-blue-700' : 'bg-white text-gray-700 hover:bg-gray-50'"
                  @click="memoFilter = 'my'"
                >
                  Mes mémos
                </button>
                <button
                  class="px-3 py-2 text-sm font-medium rounded-md transition-colors"
                  :class="memoFilter === 'all' ? 'bg-blue-100 text-blue-700' : 'bg-white text-gray-700 hover:bg-gray-50'"
                  @click="memoFilter = 'all'"
                >
                  Tous les mémos
                </button>
              </div>

              <!-- Barre de recherche -->
              <div class="flex-1 max-w-md">
                <div class="relative">
                  <SearchIcon class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    v-model="searchQuery"
                    type="text"
                    placeholder="Rechercher dans les mémos..."
                    class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <!-- Compteur de sélection -->
              <div class="text-sm text-gray-600">
                <div v-if="hasChanges" class="text-blue-600 font-medium">
                  {{ changesSummary }}
                </div>
                <div v-else class="text-gray-500">
                  Aucun changement
                </div>
              </div>
            </div>
          </div>

          <!-- Liste des mémos -->
          <div class="flex-1 overflow-y-auto p-4">
            <div v-if="filteredMemos.length === 0" class="text-center py-8">
              <p class="text-gray-500">Aucun mémo trouvé</p>
            </div>
            
            <div v-else class="space-y-2">
              <div
                v-for="memo in filteredMemos"
                :key="memo.uuid"
                class="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <!-- Checkbox -->
                <input
                  type="checkbox"
                  :checked="isMemoSelected(memo.uuid)"
                  class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  @change="toggleMemoSelection(memo.uuid)"
                />
                
                                 <!-- Informations du mémo -->
                 <div class="ml-3 flex-1">
                   <div class="flex items-center justify-between">
                     <div>
                       <div class="flex items-center space-x-2">
                         <h3 class="text-sm font-medium text-gray-900">
                           {{ memo.title || 'Sans titre' }}
                         </h3>
                         <span 
                           v-if="isMemoInCollection(memo.uuid)"
                           class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                         >
                           Dans la collection
                         </span>
                       </div>
                       <p class="text-xs text-gray-500 mt-1">
                         {{ memo.location?.site || 'Aucun lieu' }} • 
                         {{ memo.material_entity?.appellation || 'Aucune entité' }}
                       </p>
                     </div>
                    <div class="flex items-center space-x-2">
                      <!-- Badge type -->
                      <span
                        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                        :class="memo.is_model ? 'bg-purple-100 text-purple-800' : 'bg-pink-100 text-pink-800'"
                      >
                        {{ memo.is_model ? 'Modèle' : 'Fiche' }}
                      </span>
                      
                      <!-- Badge statut -->
                      <span
                        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                        :class="memo.is_draft ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'"
                      >
                        {{ memo.is_draft ? 'Brouillon' : 'Validé' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-between p-6 border-t border-gray-200">
          <div class="text-sm text-gray-600">
            {{ filteredMemos.length }} mémo(s) affiché(s)
          </div>
          <div class="flex items-center space-x-3">
            <button
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              @click="closeModal"
            >
              Annuler
            </button>
            <button
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="!hasChanges"
              @click="handleValidate"
            >
              {{ hasChanges ? `Valider les changements` : 'Aucun changement' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Memo } from '@/api/types/memos/types';
import { Search as SearchIcon, X as XIcon } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';

interface Props {
  isOpen: boolean;
  collectionName: string;
  allMemos: Memo[];
  currentUserMemos: Memo[];
  existingMemosInCollection: string[]; // UUIDs des mémos déjà dans la collection
  collectionUuid: string; // UUID de la collection pour les opérations
}

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
  'add-memos': [memoUuids: string[]];
  'remove-memos': [memoUuids: string[]];
  'update-collection': [data: { 
    added: string[]; 
    removed: string[]; 
    collectionName: string;
  }];
}>();

// État local
const searchQuery = ref('');
const memoFilter = ref<'my' | 'all'>('my');
const selectedMemos = ref<string[]>([]);
const memosToAdd = ref<string[]>([]);
const memosToRemove = ref<string[]>([]);

// Computed
const filteredMemos = computed(() => {
  let memos = memoFilter.value === 'my' ? props.currentUserMemos : props.allMemos;
  
  // Filtrer par recherche
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    memos = memos.filter(memo => {
      const title = memo.title?.toLowerCase() || '';
      const site = memo.location?.site?.toLowerCase() || '';
      const material = memo.material_entity?.appellation?.toLowerCase() || '';
      
      return title.includes(query) || site.includes(query) || material.includes(query);
    });
  }
  
  return memos;
});

// Méthodes
const isMemoInCollection = (uuid: string): boolean => {
  return props.existingMemosInCollection.includes(uuid);
};

const isMemoSelected = (uuid: string): boolean => {
  const isInCollection = isMemoInCollection(uuid);
  const isSelected = selectedMemos.value.includes(uuid);
  
  // Si le mémo est dans la collection et pas sélectionné pour suppression, il est "coché"
  // Si le mémo n'est pas dans la collection mais sélectionné pour ajout, il est "coché"
  return (isInCollection && !memosToRemove.value.includes(uuid)) || 
         (!isInCollection && isSelected);
};

const toggleMemoSelection = (uuid: string): void => {
  const isInCollection = isMemoInCollection(uuid);
  
  if (isInCollection) {
    // Le mémo est dans la collection, on gère la suppression
    const removeIndex = memosToRemove.value.indexOf(uuid);
    if (removeIndex === -1) {
      // Ajouter à la liste des mémos à supprimer
      memosToRemove.value.push(uuid);
    } else {
      // Retirer de la liste des mémos à supprimer
      memosToRemove.value.splice(removeIndex, 1);
    }
  } else {
    // Le mémo n'est pas dans la collection, on gère l'ajout
    const addIndex = selectedMemos.value.indexOf(uuid);
    if (addIndex === -1) {
      selectedMemos.value.push(uuid);
    } else {
      selectedMemos.value.splice(addIndex, 1);
    }
  }
};

// Computed pour les changements
const hasChanges = computed(() => {
  return selectedMemos.value.length > 0 || memosToRemove.value.length > 0;
});

const changesSummary = computed(() => {
  const added = selectedMemos.value.length;
  const removed = memosToRemove.value.length;
  
  if (added > 0 && removed > 0) {
    return `${added} à ajouter, ${removed} à retirer`;
  } else if (added > 0) {
    return `${added} à ajouter`;
  } else if (removed > 0) {
    return `${removed} à retirer`;
  }
  return '';
});

const closeModal = (): void => {
  emit('close');
};

const handleValidate = (): void => {
  if (hasChanges.value) {
    emit('update-collection', {
      added: selectedMemos.value,
      removed: memosToRemove.value,
      collectionName: props.collectionName
    });
  }
  closeModal();
};

// Reset selection quand le modal s'ouvre
watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    selectedMemos.value = [];
    memosToAdd.value = [];
    memosToRemove.value = [];
    searchQuery.value = '';
    memoFilter.value = 'my';
  }
});
</script> 